<!-- Home is the one page that shows the standing "Offline" pill (#3991). Its header is uncluttered
     and it is the screen the user lands on, so the pill fits without squeezing the title. Every
     other page relies on the 2px line, as before. -->
<cs-header hideType="auto" [showOfflineBadge]="true">
  <cs-header-title large>
    <span class="clickable-title" (click)="onTitleClick()">
      <span>{{ currentDayLabel + (fakeToday ? ' ' + fakeToday : '') }}</span>
      <cs-icon large name="arrow-point-down"></cs-icon>
    </span>
  </cs-header-title>
  <cs-icon-button header-right icon="settings" large (click)="handleSettingsButtonClicked($event)"></cs-icon-button>

  <cs-header-content>
    <cs-task-calendar [taskStatuses]="calendarTaskStatuses" [selectedDate]="currentDay"
      (selectedDateChange)="onCalendarDateSelected($event)">
    </cs-task-calendar>
    <div style="display: flex; justify-content: center;">
      <cs-tab [(selection)]="selectedTab" urlParam="tab">
        <cs-tab-button [value]="HomeTab.DailyLog">
          <cs-icon name="checklist"></cs-icon>
          <cs-label>{{ LL.home.tabs.dailyLog() }}</cs-label>
        </cs-tab-button>
        <cs-tab-button [value]="HomeTab.Progress">
          <cs-icon name="progress"></cs-icon>
          <cs-label>{{ LL.home.tabs.progress() }}</cs-label>
        </cs-tab-button>
      </cs-tab>

      <!-- Log Activity button for desktop -->
      @if (!hasClientSmallScreen()) {
      <cs-button style="margin-left: auto;" small class="log-activity-button" icon-right icon="arrow-point-down"
        (click)="handleLogActivityClick($event)">
        {{ LL.home.buttons.logActivity() }}
      </cs-button>
      }
    </div>
  </cs-header-content>
</cs-header>
<cs-tab-content-container [animate]="animateTabContent" [selection]="activeSlot" [isBack]="isBack"
  [swipeEnabled]="hasClientSmallScreen()" (swipeForward)="handleSwipeForward()" (swipeBack)="handleSwipeBack()">
  <!-- Daily Log Slot A -->
  <cs-tab-content class="daily-log-tab" [value]="'daylog-A'">
    <cs-content [class.active]="activeSlot === 'daylog-A'">
      @if (awaitingForBodyfatEstimate && activeDaySlot === 'A') {
      <hint-bubble once="no-nutrition-targets" [dismiss]="true" target="page-home"
        targetChild="div.dayScroll .header-content .header-nutrients">
        <p>{{ LL.home.hints.nutritionTargetsWaiting() }}</p>
      </hint-bubble>
      }

      @if (!daySlots.A.loading && getScheduledItemsFor('A')?.length === 0 && !allDailyLogItemsHidden) {
      <div class="center-message">
        {{ LL.home.messages.noScheduleForDay() }}
      </div>
      }

      <!-- Empty state when all daily log items are hidden -->
      @if (allDailyLogItemsHidden) {
      <cs-empty-state iconName="settings" [title]="LL.home.emptyState.allHidden.title()"
        [subtext]="LL.home.emptyState.allHidden.subtext()">
        <cs-button secondary small (click)="handleCustomizeClick()">
          {{ LL.home.emptyState.customizeButton() }}
        </cs-button>
      </cs-empty-state>
      }

      @if (!allDailyLogItemsHidden) {
      <cs-day-items #dayItemsA [loading]="daySlots.A.loading" [skip-animation]="activeDaySlot !== 'A'"></cs-day-items>
      }
    </cs-content>
  </cs-tab-content>

  <!-- Daily Log Slot B -->
  <cs-tab-content class="daily-log-tab" [value]="'daylog-B'">
    <cs-content [class.active]="activeSlot === 'daylog-B'">
      @if (awaitingForBodyfatEstimate && activeDaySlot === 'B') {
      <hint-bubble once="no-nutrition-targets" [dismiss]="true" target="page-home"
        targetChild="div.dayScroll .header-content .header-nutrients">
        <p>{{ LL.home.hints.nutritionTargetsWaiting() }}</p>
      </hint-bubble>
      }

      @if (!daySlots.B.loading && getScheduledItemsFor('B')?.length === 0 && !allDailyLogItemsHidden) {
      <div class="center-message">
        {{ LL.home.messages.noScheduleForDay() }}
      </div>
      }

      <!-- Empty state when all daily log items are hidden -->
      @if (allDailyLogItemsHidden) {
      <cs-empty-state iconName="settings" [title]="LL.home.emptyState.allHidden.title()"
        [subtext]="LL.home.emptyState.allHidden.subtext()">
        <cs-button secondary small (click)="handleCustomizeClick()">
          {{ LL.home.emptyState.customizeButton() }}
        </cs-button>
      </cs-empty-state>
      }

      @if (!allDailyLogItemsHidden) {
      <cs-day-items #dayItemsB [loading]="daySlots.B.loading" [skip-animation]="activeDaySlot !== 'B'"></cs-day-items>
      }
    </cs-content>
  </cs-tab-content>

  <cs-tab-content class="progress-tab" [value]="'progress'">
    <cs-content [class.active]="activeSlot === 'progress'">
      <!-- Empty state when all progress items are hidden -->
      @if (allProgressItemsHidden) {
      <cs-empty-state iconName="settings" [title]="LL.home.emptyState.allHidden.title()"
        [subtext]="LL.home.emptyState.allHidden.subtext()">
        <cs-button secondary small (click)="handleCustomizeClick()">
          {{ LL.home.emptyState.customizeButton() }}
        </cs-button>
      </cs-empty-state>
      }

      @if (!allProgressItemsHidden) {
      <div class="progress-content">
        <!-- Goal Card -->
        @if (goalData && settings.homepageShowGoalCard) {
        <cs-card class="goal-card" [attr.data-goal-type]="goalData.type">
          <cs-card-title class="with-button">
            {{ LL.home.progress.goalCard.title() }}
            <cs-button tertiary small icon="edit" (click)="handleEditGoalButtonClick($event)">
              {{ LL.common.buttons.edit() }}
            </cs-button>
          </cs-card-title>
          <cs-card-content>
            <!-- Section Templates -->
            <ng-template #targetSection>
              <div class="goal-section target-section">
                <cs-label class="section-label">{{ LL.home.progress.goalCard.target() }}</cs-label>
                <div class="section-value">
                  <span class="value-text">{{ goalData.target }}</span>
                  @if (goalData.type === GoalType.GrowthMindset) {
                  <cs-tooltip [title]="LL.home.progress.goalCard.growthMindset()">
                    {{ LL.home.progress.goalCard.growthMindsetInfo() }}
                    <help-link helpId="@help:references.body-recomposition">{{ LL.common.buttons.learnMore() }}</help-link>
                  </cs-tooltip>
                  }
                </div>
              </div>
            </ng-template>
            <ng-template #progressSection>
              @if (goalData.progress) {
              <div class="goal-section progress-section">
                <cs-label class="section-label">{{ LL.home.progress.goalCard.progress() }}</cs-label>
                <div class="progress-bar-container">
                  <div class="progress-bar">
                    <div class="progress-bar-background"></div>
                    <div class="progress-bar-fill" [style.width.%]="goalProgressPercent">
                    </div>
                  </div>
                  <div class="progress-labels">
                    <span class="progress-label-start">{{ LL.home.progress.goalCard.start() }}: {{
                      goalData.progress.start }}</span>
                    <span class="progress-label-goal">{{ LL.home.progress.goalCard.goal() }}: {{
                      goalData.progress.goal }}</span>
                  </div>
                </div>
              </div>
              }
            </ng-template>
            <ng-template #statusSection>
              @if (goalData.status) {
              <div class="goal-section status-section">
                <cs-label class="section-label">{{ LL.home.progress.goalCard.progress() }}</cs-label>
                <div class="status-value"
                  [class.off-track]="!goalData.status.isOnTrack && !goalData.status.isIndeterminate"
                  [class.indeterminate]="goalData.status.isIndeterminate">
                  @if (goalData.status.isOnTrack) {
                  <cs-icon name="checkmark"></cs-icon>
                  } @else {
                  <cs-icon name="warning"></cs-icon>
                  }
                  <span class="value-text">{{ goalData.status.text }}</span>
                </div>
              </div>
              }
            </ng-template>
            <ng-template #resultSection>
              @if (goalData.result) {
              <div class="goal-section result-section">
                <cs-label class="section-label">
                  @if (goalData.type === GoalType.FatLoss) {
                  <span>{{ LL.home.progress.goalCard.fatLoss() }}</span>
                  }
                  @if (goalData.type === GoalType.WeightLoss) {
                  <span>{{ LL.home.progress.goalCard.weightLoss() }}</span>
                  }
                  @if (goalData.type === GoalType.WeightGain) {
                  <span>{{ LL.home.progress.goalCard.weightGain() }}</span>
                  }
                  <cs-tooltip x-small iconType="info" [text]="LL.home.progress.goalCard.estimatedValueTooltip()"></cs-tooltip>
                </cs-label>
                <div class="section-value">
                  <span class="value-text">{{ goalData.result.currentValue }}</span>
                  <span class="change-text" [ngClass]="'trend-' + goalData.result.trendQuality">
                    {{ goalData.result.change }}
                    <cs-icon
                      [name]="goalData.result.trend === 'up' ? 'trending-up' : goalData.result.trend === 'down' ? 'trending-down' : 'trending-flat'"></cs-icon>
                  </span>
                </div>
              </div>
              }
            </ng-template>
            <!-- Desktop: Dynamic layout based on goal type -->
            <div class="goal-sections-desktop" [class.two-sections]="goalData.type === GoalType.GrowthMindset">
              <ng-container *ngTemplateOutlet="targetSection"></ng-container>
              <ng-container *ngTemplateOutlet="progressSection"></ng-container>
              <ng-container *ngTemplateOutlet="statusSection"></ng-container>
              <ng-container *ngTemplateOutlet="resultSection"></ng-container>
            </div>
            <!-- Mobile: Dynamic layout based on goal type -->
            <div class="goal-sections-mobile" [class.two-sections]="goalData.type === GoalType.GrowthMindset">
              <!-- Fat Loss & Weight Loss: Target + Result in one row, Progress full width below -->
              @if (goalData.type !== GoalType.GrowthMindset) {
              <div class="goal-top-row">
                <ng-container *ngTemplateOutlet="targetSection"></ng-container>
                <ng-container *ngTemplateOutlet="resultSection"></ng-container>
              </div>
              }
              @if (goalData.type !== GoalType.GrowthMindset) {
              <ng-container [ngTemplateOutlet]="progressSection"></ng-container>
              }
              <!-- Growth Mindset: Target and Status stacked vertically -->
              @if (goalData.type === GoalType.GrowthMindset) {
              <ng-container *ngTemplateOutlet="targetSection"></ng-container>
              <ng-container *ngTemplateOutlet="statusSection"></ng-container>
              }
            </div>
            <!-- Indeterminate: per-message explanations -->
            @if (goalData?.status?.isIndeterminate) {
            <cs-text-alert type="neutral">
              @if (recentAdjustmentBannerText) {
              {{ recentAdjustmentBannerText }}
              } @else if (goalData.status.indeterminateMessage === PlanUpdateUserMessage.NotEnoughData) {
              {{ LL.home.progress.goalCard.notEnoughData() }}
              } @else if (goalData.status.indeterminateMessage === PlanUpdateUserMessage.MeasureMoreOften) {
              {{ LL.home.progress.goalCard.measureMoreOften() }}
              } @else if (goalData.status.indeterminateMessage === PlanUpdateUserMessage.DataErratic) {
              {{ LL.home.progress.goalCard.dataErratic() }}
              } @else if (goalData.status.indeterminateMessage === PlanUpdateUserMessage.WeightTooFlat) {
              {{ LL.home.progress.goalCard.weightTooFlat() }}
              } @else if (goalData.status.indeterminateMessage === PlanUpdateUserMessage.InsufficientFatnessConfidence) {
              {{ LL.home.progress.goalCard.insufficientFatnessConfidence() }}
              }
              <help-link helpId="@help:progress.undetermined">{{ LL.common.buttons.learnMore() }}</help-link>
            </cs-text-alert>
            }
            <!-- Off Track Warning -->
            @if (goalData?.status && !goalData.status.isOnTrack && !goalData.status.isIndeterminate) {
            <cs-text-alert type="warning">
              @if (goalData.status.offTrackReason === AdjustmentActionReason.TooMuchWeightLoss) {
              {{ goalData.isCutting ? LL.common.offTrack.tooMuchWeightLoss.cutting.summary() :
              LL.common.offTrack.tooMuchWeightLoss.bulking.summary() }}
              }
              @if (goalData.status.offTrackReason === AdjustmentActionReason.TooMuchFatGain) {
              {{ goalData.isCutting ? LL.common.offTrack.tooMuchFatGain.cutting.summary() :
              LL.common.offTrack.tooMuchFatGain.bulking.summary() }}
              }
              @if (goalData.status.offTrackReason === AdjustmentActionReason.NotEnoughFatLoss) {
              {{ LL.common.offTrack.notEnoughFatLoss.cutting.summary() }}
              }
              @if (goalData.status.offTrackReason === AdjustmentActionReason.NotEnoughWeightGain) {
              {{ LL.common.offTrack.notEnoughWeightGain.bulking.summary() }}
              }
              @if (!goalData.status.offTrackReason) {
              {{ LL.common.offTrack.generic.summary() }}
              }
              <help-link helpId="@help:progress.off-track">{{ LL.common.buttons.learnMore() }}</help-link>
            </cs-text-alert>
            }
          </cs-card-content>
        </cs-card>
        }
        <!-- Charts Row -->
        @if (settings.homepageShowBodyComposition || settings.homepageShowNutritionChart) {
        <div class="charts-row">
          <!-- Body Composition Chart -->
          @if (settings.homepageShowBodyComposition) {
          <cs-card class="chart-card" hoverable (click)="handleBodyCompositionChartClick()">
            <cs-card-title>{{ LL.home.progress.bodyComposition.title() }}</cs-card-title>
            <cs-card-content>
              <cs-progress-chart [compact]="true" [series]="bodyCompositionSeries" [data]="bodyCompositionData"
                [axisConfig]="bodyCompositionAxisConfig" [timePeriod]="'month'" [startDate]="bodyCompositionStartDate"
                [endDate]="bodyCompositionEndDate" [trends]="bodyCompositionTrends" [isLoading]="bodyCompDataService.isLoading()">
              </cs-progress-chart>
            </cs-card-content>
          </cs-card>
          }
          <!-- Nutrition Chart -->
          @if (settings.homepageShowNutritionChart) {
          <cs-card class="chart-card" hoverable (click)="handleNutritionChartClick()">
            <cs-card-title>{{ LL.home.progress.nutrition.title() }}</cs-card-title>
            <cs-card-content>
              <cs-progress-chart [compact]="true" [series]="nutritionSeries" [data]="nutritionData"
                [axisConfig]="nutritionAxisConfig" [timePeriod]="'week'" [startDate]="nutritionStartDate"
                [endDate]="nutritionEndDate" [isLoading]="nutritionDataService.isLoading()">
              </cs-progress-chart>
            </cs-card-content>
          </cs-card>
          }
        </div>
        }
        <!-- Coaching Tips Card -->
        @if (settings.homepageShowCoachingTips) {
        <cs-card class="coaching-tips-card">
          <cs-card-content>
            <div class="coaching-tips-container">
              <div class="icon-wrapper">
                <div class="icon-background">
                  <cs-icon name="school"></cs-icon>
                </div>
              </div>
              <div class="content-column">
                <div class="title-row">
                  <h3 class="card-title">{{ LL.home.progress.coachingTips.title() }}</h3>
                </div>
                <ul class="coaching-tips-list">
                  @if (showNoTipsMessage) {
                  <li>{{ LL.home.progress.coachingTips.noTips() }}</li>
                  }
                  @for (tip of coachingTips; track tip) {
                  <li>
                    {{ scheduleTipText(tip) }}&nbsp;<a class="learn-more-link" (click)="handleTipLearnMore(tip)">{{
                      LL.common.buttons.learnMore() }}</a>
                  </li>
                  }
                </ul>
              </div>
            </div>
          </cs-card-content>
        </cs-card>
        }
      </div>
      }
    </cs-content>
  </cs-tab-content>
</cs-tab-content-container>